import { createContext, useContext } from 'react';
import { isConsentGranted } from './consent';
import { TUTORIAL_TRANSLATIONS } from './onboarding/tutorialTranslations';

export const SETTINGS_STORAGE_KEY = 'adpulsePreferences';

export const DEFAULT_SETTINGS = {
  language: 'en',
  theme: 'light',
  notifications: true,
};

export const RTL_LANGUAGES = new Set(['ar', 'fa', 'he', 'ur']);
export const isRtlLanguage = (language) => RTL_LANGUAGES.has(String(language || '').split('-')[0].toLowerCase());

export const TRANSLATIONS = {
  en: {
    ...TUTORIAL_TRANSLATIONS.en,
    'nav.home': 'Home',
    'nav.contact': 'Contact',
    'nav.settings': 'Settings',
    'nav.launchApp': 'Launch App',
    'nav.campaigns': 'Campaigns',
    'nav.brandHub': 'Brand Hub',
    'nav.graphicsSuite': 'Graphics Suite',
    'nav.analytics': 'Analytics',
    'nav.signIn': 'Sign in',
    'nav.site': 'Site',
    'nav.platformLabel': 'AWN GMBH AI PLATFORM',
    'settings.title': 'Settings',
    'settings.subtitle': 'Personalize language, display, and app notifications.',
    'settings.language': 'Language',
    'settings.languageHelp': 'Choose the interface language.',
    'settings.english': 'English',
    'settings.german': 'German',
    'settings.theme': 'Theme',
    'settings.themeHelp': 'Switch the whole frontend between light and dark mode.',
    'settings.light': 'Light Mode',
    'settings.dark': 'Dark Mode',
    'settings.notifications': 'Notifications',
    'settings.notificationsHelp': 'Show frontend toast messages for saves, errors, and updates.',
    'settings.reset': 'Reset Preferences',
    'settings.close': 'Close settings',
    'settings.saved': 'Preferences saved',
    'app.workspaceEyebrow': 'Interactive Engine',
    'app.workspaceTitle': 'Campaign Studio',
    'app.workspaceDescription': 'Compose a brief, ground it on your brand knowledge, and generate ready-to-ship creatives, copy and a full content plan - all in one workspace.',
    'app.footerRights': 'All rights reserved.',
    'app.footerTagline': 'Proprietary high-fidelity ad generation platform and copywriting workflow engines.',
    'app.legalNotice': 'Legal notice',
    'app.privacyPolicy': 'Privacy Policy',
    'app.cookiePolicy': 'Cookie Policy',
    'app.terms': 'Terms',
    'app.cookiePreferences': 'Cookie preferences',
    'app.legalLinksLabel': 'Legal and privacy links',
    'hero.title': 'UNLEASH CREATIVITY. INSTANTLY.',
    'hero.titleVariant1': 'CREATE ADS. INSTANTLY.',
    'hero.titleVariant2': 'IDEAS TO CAMPAIGNS.',
    'hero.titleVariant3': 'ON-BRAND VISUALS. FAST.',
    'hero.subtitle': 'Generate premium 3D assets, ads, posters, and marketing visuals effortlessly with the power of advanced AI.',
    'hero.start': 'Start Generating',
    'landing.capabilitiesLabel': 'AWN AdPulse capabilities',
    'landing.capabilityBrand': 'Brand-grounded generation',
    'landing.capabilityCopy': 'Campaign copy',
    'landing.capabilityImages': 'AI image direction',
    'landing.capabilityVideo': 'Short-form video',
    'landing.capabilityExperiments': 'Prompt experiments',
    'landing.capabilityScoring': 'Quality scoring',
    'landing.previewBrandGrounded': 'Brand grounded',
    'landing.previewBrandFit': '92% brand fit',
    'landing.previewAiReady': 'AI ready',
    'landing.previewCampaignSystem': 'CAMPAIGN SYSTEM',
    'landing.previewProductLaunch': 'Lumina product launch',
    'landing.previewGenerating': 'Generating',
    'landing.previewStayFlow': 'STAY IN YOUR FLOW.',
    'landing.previewGlowBottle': 'Glow Bottle',
    'landing.previewCampaignCopy': 'CAMPAIGN COPY',
    'landing.previewHydration': 'Hydration that keeps up.',
    'landing.previewQualityScore': 'QUALITY SCORE',
    'landing.previewReadyRefine': 'Ready to refine',
    'landing.previewBrief': 'Brief',
    'landing.previewCopy': 'Copy',
    'landing.previewCreative': 'Creative',
    'landing.previewQualityCheck': 'Quality check',
    'landing.visualBrandProfile': 'Brand profile',
    'landing.visualBrandDetails': 'Voice · audience · mission',
    'landing.visualProductKnowledge': 'Product knowledge',
    'landing.visualProductDetails': 'Benefits · market · proof',
    'landing.visualApprovedDocs': 'Approved documents',
    'landing.visualDocDetails': 'Brochure · guidelines · PDF',
    'landing.visualVisual': 'Visual',
    'landing.visualVideo': 'Video',
    'landing.visualCopy': 'Copy',
    'landing.visualBrandFit': 'Brand fit',
    'landing.visualClarity': 'Clarity',
    'landing.visualSafety': 'Safety',
    'landing.visualMultiFormat': 'Multi-format campaign kit',
    'landing.introKicker': 'Built for modern marketing teams',
    'landing.introTitle': 'From brand knowledge to a campaign your team can actually use.',
    'landing.introCopy': 'AWN AdPulse brings campaign strategy, copy, visual direction, short-form video planning and quality evaluation into one focused AI workspace.',
    'landing.introCta': 'Explore Campaign Studio',
    'landing.featuresKicker': 'One connected creative system',
    'landing.featuresTitle': 'Professional campaign creation without the fragmented workflow.',
    'landing.featuresCopy': 'Everything needed to move from an idea to a review-ready campaign—connected by the same brand and strategy context.',
    'landing.featureBrandLabel': 'Brand intelligence',
    'landing.featureBrandTitle': 'Creative that starts with your brand',
    'landing.featureBrandCopy': 'Ground every campaign in company profiles, product knowledge and approved source material—not generic model guesses.',
    'landing.featureCampaignLabel': 'Campaign Studio',
    'landing.featureCampaignTitle': 'One brief. A complete launch system.',
    'landing.featureCampaignCopy': 'Generate positioning, ad copy, social content, emails, creative direction and a practical content plan in one flow.',
    'landing.featureQualityLabel': 'Quality control',
    'landing.featureQualityTitle': 'Evaluate before you publish',
    'landing.featureQualityCopy': 'Compare relevance, clarity, brand fit, conversion potential and ethical safety with transparent research notes.',
    'landing.featureSuiteLabel': 'Creative Suite',
    'landing.featureSuiteTitle': 'Finish and export in the same workspace',
    'landing.featureSuiteCopy': 'Review visual directions, adapt copy, regenerate individual fields and prepare channel-ready campaign assets.',
    'landing.workflowKicker': 'A clearer path to launch',
    'landing.workflowTitle': 'A premium workflow that stays understandable.',
    'landing.workflowCopy': 'Maintain creative control at every stage. The system shows what it is using, what it is creating and why each direction should work.',
    'landing.workflowGroundTitle': 'Ground the brand',
    'landing.workflowGroundCopy': 'Select a saved company and product, or start from a focused manual brief.',
    'landing.workflowChooseTitle': 'Choose the campaign',
    'landing.workflowChooseCopy': 'Set the audience, goal, channel, tone and art direction for this launch.',
    'landing.workflowGenerateTitle': 'Generate and compare',
    'landing.workflowGenerateCopy': 'Create a single direction or compare multiple persuasion frameworks side by side.',
    'landing.workflowReviewTitle': 'Review and ship',
    'landing.workflowReviewCopy': 'Evaluate quality, refine individual fields and export a presentation-ready campaign.',
    'landing.proofKicker': 'DESIGNED FOR DECISIONS',
    'landing.proofTitle': 'Compare creative logic—not just pretty outputs.',
    'landing.proofFrameworks': 'persuasion frameworks',
    'landing.proofDimensions': 'quality dimensions',
    'landing.proofSystem': 'review-ready system',
    'landing.finalKicker': 'Your next campaign starts here',
    'landing.finalTitle': 'Move from scattered tools to one focused creative engine.',
    'landing.finalCopy': 'Build brand-grounded campaigns, compare the strongest direction and prepare every idea for human review.',
    'landing.finalCta': 'Start building',
    'landing.diveReady': 'Ready',
    'landing.diveTo': 'to',
    'landing.diveIn': 'Dive In?',
    'landing.diveLabel': 'Ready to Dive In?',
    'landing.diveCopy': 'Turn one focused brief into a complete, review-ready campaign. Strategy, creative direction and channel-ready content stay connected.',
    'landing.diveCta': 'Start Building',
    'cta.eyebrow': 'Ready when you are',
    'cta.title': 'Turn your brand knowledge into campaigns today',
    'cta.description': 'Spin up a company profile, upload your documents, and generate your first grounded campaign in minutes.',
    'saved.eyebrow': 'Local History',
    'saved.title': 'Saved campaigns',
    'saved.description': 'Click any campaign to reopen it in the studio.',
    'saved.empty': 'No saved campaigns yet.',
    'dashboard.eyebrow': 'Insights',
    'dashboard.title': 'Your Analytics',
    'dashboard.description': 'Track your generated campaigns, AI quality scores and human feedback in one place.',
    'dashboard.campaignsGenerated': 'Campaigns generated',
    'dashboard.avgAiQuality': 'Avg AI quality',
    'dashboard.avgHumanRating': 'Avg human rating',
    'dashboard.feedbackEntries': 'Feedback entries',
    'dashboard.reviews': 'reviews',
    'dashboard.strategiesUsed': 'Strategies used',
    'dashboard.platformsTargeted': 'Platforms targeted',
    'dashboard.noData': 'No data yet.',
    'dashboard.noCampaigns': 'No campaigns yet',
    'dashboard.noCampaignsText': 'Generate your first campaign to start seeing analytics here.',
    'dashboard.openStudio': 'Open Campaign Studio',
    'contact.eyebrow': 'Get in touch',
    'contact.title': 'Request access or a guided demo',
    'contact.description': 'Tell us about your brand and goals. Our team will reach out within one business day to get you generating.',
    'contact.name': 'Full Name',
    'contact.email': 'Corporate Email',
    'contact.company': 'Company / Organization',
    'contact.budget': 'Project Budget Range',
    'contact.interested': "I'm interested in",
    'contact.brief': 'Tell us about your brand & goals',
    'contact.submit': 'Request access',
    'contact.transmitting': 'Transmitting inquiry...',
    'contact.successTitle': 'Request received successfully',
    'contact.successText': 'Thanks for reaching out. The AWN AdPulse team has your details and will contact you shortly to set up your workspace and a guided demo.',
    'contact.sectionLabel': 'Contact us',
    'contact.namePlaceholder': 'e.g., Katherine Bell',
    'contact.emailPlaceholder': 'e.g., katherine@company.com',
    'contact.companyPlaceholder': 'e.g., Acme Corp',
    'contact.briefPlaceholder': "Your industry, products, target audience, and what you'd like to generate first...",
    'contact.privacyBefore': 'I have read the',
    'contact.privacyAfter': 'and understand how this contact request will be handled.',
    'contact.sendError': 'Could not send your request. Please try again.',
    'studio.brief': 'Campaign Brief',
    'studio.compose': 'Compose your campaign',
    'studio.reset': 'Reset',
    'studio.step': 'Step',
    'studio.brandProduct': 'Brand & Product',
    'studio.audienceValue': 'Audience & Value',
    'studio.strategyOutput': 'Strategy & Output',
    'studio.choose': 'Choose how to generate',
    'studio.chooseHelp': 'Pick a saved company to generate from its stored profile, or leave it on manual to use only what you type.',
    'studio.company': 'Company',
    'studio.product': 'Product',
    'studio.manualInput': 'Manual input (no saved company)',
    'studio.allProducts': 'All / first product',
    'studio.savedMode': 'Saved Company Mode',
    'studio.manualMode': 'Manual Input Mode',
    'studio.manualModeHelp': 'generation uses only the details you type below. No saved or previously selected company data is used.',
    'studio.brandName': 'Brand name',
    'studio.productName': 'Product name',
    'studio.productDescription': 'Product description',
    'studio.productDescriptionPlaceholder': 'Describe what the product does, who it helps, and its main use.',
    'studio.brandMemory': 'Brand memory (optional)',
    'studio.brandColors': 'Brand colors',
    'studio.brandFont': 'Brand font',
    'studio.knowledgeBase': 'Knowledge base',
    'studio.targetAudience': 'Target audience',
    'studio.targetAudiencePlaceholder': 'Who is most likely to need or buy this product?',
    'studio.keyBenefits': 'Key benefits',
    'studio.keyBenefitsPlaceholder': 'List the strongest customer benefits, separated by commas.',
    'studio.specialOffer': 'Special offer',
    'studio.specialOfferPlaceholder': 'Optional — e.g. a launch discount, free trial, or limited-time bundle',
    'studio.reference': 'Competitor / reference',
    'studio.referencePlaceholder': 'Optional — e.g. a visual mood, category leader, or campaign style',
    'studio.visualStyle': 'Desired visual style',
    'studio.visualInstructions': 'Additional visual instructions',
    'studio.visualInstructionsPlaceholder': 'Describe required subjects, setting, composition, materials, lighting, or product behavior.',
    'studio.negativeInstructions': 'Things to avoid (negative instructions)',
    'studio.negativeInstructionsPlaceholder': 'e.g. no people, no smartphones, avoid red backgrounds, no outdoor setting',
    'studio.negativeInstructionsHelp': 'These exclusions are added to the final image prompt and the video model’s negative prompt.',
    'studio.goal': 'Goal',
    'studio.tone': 'Tone',
    'studio.strategy': 'Strategy',
    'studio.platform': 'Platform',
    'studio.language': 'Language',
    'studio.renderStyle': 'Render style',
    'studio.artDirection': 'Art direction',
    'studio.imageCreativeOptions': 'Image creative options',
    'studio.videoDuration': 'Video duration',
    'studio.videoSize': 'Video output size',
    'studio.back': 'Back',
    'studio.continue': 'Continue',
    'studio.experiment': 'A/B Experiment',
    'studio.running': 'Running...',
    'studio.generate': 'Generate Campaign',
    'studio.generating': 'Generating...',
  },
  de: {
    ...TUTORIAL_TRANSLATIONS.de,
    'nav.home': 'Start',
    'nav.contact': 'Kontakt',
    'nav.settings': 'Einstellungen',
    'nav.launchApp': 'App starten',
    'nav.campaigns': 'Kampagnen',
    'nav.brandHub': 'Brand Hub',
    'nav.graphicsSuite': 'Grafik-Suite',
    'nav.analytics': 'Analysen',
    'nav.signIn': 'Anmelden',
    'nav.site': 'Website',
    'nav.platformLabel': 'AWN GMBH KI-PLATTFORM',
    'settings.title': 'Einstellungen',
    'settings.subtitle': 'Passe Sprache, Darstellung und App-Benachrichtigungen an.',
    'settings.language': 'Sprache',
    'settings.languageHelp': 'Wahle die Sprache der Benutzeroberflache.',
    'settings.english': 'Englisch',
    'settings.german': 'Deutsch',
    'settings.theme': 'Darstellung',
    'settings.themeHelp': 'Wechsle das gesamte Frontend zwischen hellem und dunklem Modus.',
    'settings.light': 'Heller Modus',
    'settings.dark': 'Dunkler Modus',
    'settings.notifications': 'Benachrichtigungen',
    'settings.notificationsHelp': 'Toast-Meldungen fur Speichern, Fehler und Updates anzeigen.',
    'settings.reset': 'Einstellungen zurucksetzen',
    'settings.close': 'Einstellungen schliessen',
    'settings.saved': 'Einstellungen gespeichert',
    'app.workspaceEyebrow': 'Interaktive Engine',
    'app.workspaceTitle': 'Kampagnen-Studio',
    'app.workspaceDescription': 'Erstelle ein Briefing, nutze dein Markenwissen und generiere versandfertige Creatives, Texte und einen vollstandigen Content-Plan - alles in einem Workspace.',
    'app.footerRights': 'Alle Rechte vorbehalten.',
    'app.footerTagline': 'Proprietare Plattform fur hochqualitative Anzeigengenerierung und Copywriting-Workflows.',
    'app.legalNotice': 'Impressum',
    'app.privacyPolicy': 'Datenschutzerklärung',
    'app.cookiePolicy': 'Cookie-Richtlinie',
    'app.terms': 'Nutzungsbedingungen',
    'app.cookiePreferences': 'Cookie-Einstellungen',
    'app.legalLinksLabel': 'Rechtliche Hinweise und Datenschutz',
    'hero.title': 'ENTFESSE KREATIVITAT. SOFORT.',
    'hero.titleVariant1': 'ANZEIGEN. SOFORT.',
    'hero.titleVariant2': 'IDEEN ZU KAMPAGNEN.',
    'hero.titleVariant3': 'MARKENVISUALS. SCHNELL.',
    'hero.subtitle': 'Erstelle Premium-3D-Assets, Anzeigen, Poster und Marketing-Visuals muhelos mit fortschrittlicher KI.',
    'hero.start': 'Generieren starten',
    'landing.capabilitiesLabel': 'Funktionen von AWN AdPulse',
    'landing.capabilityBrand': 'Markenbasierte Generierung',
    'landing.capabilityCopy': 'Kampagnentexte',
    'landing.capabilityImages': 'KI-Bildregie',
    'landing.capabilityVideo': 'Kurzvideos',
    'landing.capabilityExperiments': 'Prompt-Experimente',
    'landing.capabilityScoring': 'Qualitätsbewertung',
    'landing.previewBrandGrounded': 'Markenbasiert',
    'landing.previewBrandFit': '92 % Markenpassung',
    'landing.previewAiReady': 'KI bereit',
    'landing.previewCampaignSystem': 'KAMPAGNENSYSTEM',
    'landing.previewProductLaunch': 'Lumina-Produkteinführung',
    'landing.previewGenerating': 'Wird generiert',
    'landing.previewStayFlow': 'BLEIB IN DEINEM FLOW.',
    'landing.previewGlowBottle': 'Glow-Flasche',
    'landing.previewCampaignCopy': 'KAMPAGNENTEXT',
    'landing.previewHydration': 'Hydration, die mithält.',
    'landing.previewQualityScore': 'QUALITÄTSSCORE',
    'landing.previewReadyRefine': 'Bereit zur Verfeinerung',
    'landing.previewBrief': 'Briefing',
    'landing.previewCopy': 'Text',
    'landing.previewCreative': 'Creative',
    'landing.previewQualityCheck': 'Qualitätsprüfung',
    'landing.visualBrandProfile': 'Markenprofil',
    'landing.visualBrandDetails': 'Stimme · Zielgruppe · Mission',
    'landing.visualProductKnowledge': 'Produktwissen',
    'landing.visualProductDetails': 'Nutzen · Markt · Belege',
    'landing.visualApprovedDocs': 'Freigegebene Dokumente',
    'landing.visualDocDetails': 'Broschüre · Richtlinien · PDF',
    'landing.visualVisual': 'Visual',
    'landing.visualVideo': 'Video',
    'landing.visualCopy': 'Text',
    'landing.visualBrandFit': 'Markenpassung',
    'landing.visualClarity': 'Klarheit',
    'landing.visualSafety': 'Sicherheit',
    'landing.visualMultiFormat': 'Kampagnen-Kit für mehrere Formate',
    'landing.introKicker': 'Für moderne Marketingteams entwickelt',
    'landing.introTitle': 'Vom Markenwissen zur Kampagne, die dein Team wirklich nutzen kann.',
    'landing.introCopy': 'AWN AdPulse vereint Kampagnenstrategie, Texte, visuelle Regie, Kurzvideo-Planung und Qualitätsbewertung in einem fokussierten KI-Workspace.',
    'landing.introCta': 'Kampagnen-Studio entdecken',
    'landing.featuresKicker': 'Ein vernetztes Kreativsystem',
    'landing.featuresTitle': 'Professionelle Kampagnenerstellung ohne fragmentierte Abläufe.',
    'landing.featuresCopy': 'Alles für den Weg von der Idee zur prüfungsreifen Kampagne—verbunden durch denselben Marken- und Strategiekontext.',
    'landing.featureBrandLabel': 'Markenintelligenz',
    'landing.featureBrandTitle': 'Creatives, die bei deiner Marke beginnen',
    'landing.featureBrandCopy': 'Verankere jede Kampagne in Unternehmensprofilen, Produktwissen und freigegebenen Quellen statt in generischen Modellannahmen.',
    'landing.featureCampaignLabel': 'Kampagnen-Studio',
    'landing.featureCampaignTitle': 'Ein Briefing. Ein komplettes Launch-System.',
    'landing.featureCampaignCopy': 'Generiere Positionierung, Anzeigentexte, Social Content, E-Mails, Kreativregie und einen praktischen Content-Plan in einem Ablauf.',
    'landing.featureQualityLabel': 'Qualitätskontrolle',
    'landing.featureQualityTitle': 'Prüfen, bevor du veröffentlichst',
    'landing.featureQualityCopy': 'Vergleiche Relevanz, Klarheit, Markenpassung, Conversion-Potenzial und ethische Sicherheit mit transparenten Analysen.',
    'landing.featureSuiteLabel': 'Creative Suite',
    'landing.featureSuiteTitle': 'Im selben Workspace fertigstellen und exportieren',
    'landing.featureSuiteCopy': 'Prüfe visuelle Richtungen, passe Texte an, generiere einzelne Felder neu und bereite kanaloptimierte Kampagnenassets vor.',
    'landing.workflowKicker': 'Ein klarerer Weg zum Launch',
    'landing.workflowTitle': 'Ein hochwertiger Workflow, der verständlich bleibt.',
    'landing.workflowCopy': 'Behalte in jeder Phase die kreative Kontrolle. Das System zeigt, was es verwendet, was es erstellt und warum jede Richtung funktionieren sollte.',
    'landing.workflowGroundTitle': 'Marke fundieren',
    'landing.workflowGroundCopy': 'Wähle ein gespeichertes Unternehmen und Produkt oder starte mit einem fokussierten manuellen Briefing.',
    'landing.workflowChooseTitle': 'Kampagne festlegen',
    'landing.workflowChooseCopy': 'Bestimme Zielgruppe, Ziel, Kanal, Ton und Art Direction für diesen Launch.',
    'landing.workflowGenerateTitle': 'Generieren und vergleichen',
    'landing.workflowGenerateCopy': 'Erstelle eine Richtung oder vergleiche mehrere Überzeugungsmodelle direkt miteinander.',
    'landing.workflowReviewTitle': 'Prüfen und veröffentlichen',
    'landing.workflowReviewCopy': 'Bewerte die Qualität, verfeinere einzelne Felder und exportiere eine präsentationsreife Kampagne.',
    'landing.proofKicker': 'FÜR ENTSCHEIDUNGEN ENTWICKELT',
    'landing.proofTitle': 'Vergleiche kreative Logik—nicht nur schöne Ergebnisse.',
    'landing.proofFrameworks': 'Überzeugungsmodelle',
    'landing.proofDimensions': 'Qualitätsdimensionen',
    'landing.proofSystem': 'prüfungsreifes System',
    'landing.finalKicker': 'Deine nächste Kampagne startet hier',
    'landing.finalTitle': 'Von verstreuten Tools zu einer fokussierten Kreativ-Engine.',
    'landing.finalCopy': 'Erstelle markenbasierte Kampagnen, vergleiche die stärkste Richtung und bereite jede Idee für die menschliche Prüfung vor.',
    'landing.finalCta': 'Jetzt erstellen',
    'landing.diveReady': 'Bereit',
    'landing.diveTo': 'zum',
    'landing.diveIn': 'Eintauchen?',
    'landing.diveLabel': 'Bereit zum Eintauchen?',
    'landing.diveCopy': 'Verwandle ein fokussiertes Briefing in eine vollständige, prüfungsreife Kampagne. Strategie, Kreativregie und kanaloptimierte Inhalte bleiben verbunden.',
    'landing.diveCta': 'Jetzt starten',
    'cta.eyebrow': 'Bereit, wenn du es bist',
    'cta.title': 'Verwandle Markenwissen heute in Kampagnen',
    'cta.description': 'Erstelle ein Unternehmensprofil, lade Dokumente hoch und generiere in Minuten deine erste fundierte Kampagne.',
    'saved.eyebrow': 'Lokaler Verlauf',
    'saved.title': 'Gespeicherte Kampagnen',
    'saved.description': 'Klicke auf eine Kampagne, um sie im Studio erneut zu offnen.',
    'saved.empty': 'Noch keine gespeicherten Kampagnen.',
    'dashboard.eyebrow': 'Einblicke',
    'dashboard.title': 'Deine Analysen',
    'dashboard.description': 'Verfolge generierte Kampagnen, KI-Qualitatsscores und menschliches Feedback an einem Ort.',
    'dashboard.campaignsGenerated': 'Generierte Kampagnen',
    'dashboard.avgAiQuality': 'Durchschn. KI-Qualitat',
    'dashboard.avgHumanRating': 'Durchschn. Bewertung',
    'dashboard.feedbackEntries': 'Feedback-Eintrage',
    'dashboard.reviews': 'Bewertungen',
    'dashboard.strategiesUsed': 'Genutzte Strategien',
    'dashboard.platformsTargeted': 'Zielplattformen',
    'dashboard.noData': 'Noch keine Daten.',
    'dashboard.noCampaigns': 'Noch keine Kampagnen',
    'dashboard.noCampaignsText': 'Generiere deine erste Kampagne, um hier Analysen zu sehen.',
    'dashboard.openStudio': 'Kampagnen-Studio offnen',
    'contact.eyebrow': 'Kontakt',
    'contact.title': 'Zugang oder gefuhrte Demo anfragen',
    'contact.description': 'Erzahle uns von deiner Marke und deinen Zielen. Unser Team meldet sich innerhalb eines Werktags.',
    'contact.name': 'Vollstandiger Name',
    'contact.email': 'Geschaftliche E-Mail',
    'contact.company': 'Unternehmen / Organisation',
    'contact.budget': 'Projektbudget',
    'contact.interested': 'Ich interessiere mich fur',
    'contact.brief': 'Erzahle uns von deiner Marke und deinen Zielen',
    'contact.submit': 'Zugang anfragen',
    'contact.transmitting': 'Anfrage wird gesendet...',
    'contact.successTitle': 'Anfrage erfolgreich erhalten',
    'contact.successText': 'Danke fur deine Anfrage. Das AWN AdPulse Team hat deine Angaben erhalten und meldet sich in Kurze fur Workspace und Demo.',
    'contact.sectionLabel': 'Kontakt',
    'contact.namePlaceholder': 'z. B. Katherine Bell',
    'contact.emailPlaceholder': 'z. B. katherine@unternehmen.de',
    'contact.companyPlaceholder': 'z. B. Muster GmbH',
    'contact.briefPlaceholder': 'Deine Branche, Produkte, Zielgruppe und was du zuerst generieren möchtest ...',
    'contact.privacyBefore': 'Ich habe die',
    'contact.privacyAfter': 'gelesen und verstanden, wie diese Kontaktanfrage verarbeitet wird.',
    'contact.sendError': 'Deine Anfrage konnte nicht gesendet werden. Bitte versuche es erneut.',
    'studio.brief': 'Kampagnen-Briefing',
    'studio.compose': 'Kampagne erstellen',
    'studio.reset': 'Zurucksetzen',
    'studio.step': 'Schritt',
    'studio.brandProduct': 'Marke & Produkt',
    'studio.audienceValue': 'Zielgruppe & Nutzen',
    'studio.strategyOutput': 'Strategie & Ausgabe',
    'studio.choose': 'Generierungsmodus wahlen',
    'studio.chooseHelp': 'Wahle ein gespeichertes Unternehmen oder nutze nur die manuell eingegebenen Details.',
    'studio.company': 'Unternehmen',
    'studio.product': 'Produkt',
    'studio.manualInput': 'Manuelle Eingabe (kein gespeichertes Unternehmen)',
    'studio.allProducts': 'Alle / erstes Produkt',
    'studio.savedMode': 'Gespeicherter Unternehmensmodus',
    'studio.manualMode': 'Manueller Eingabemodus',
    'studio.manualModeHelp': 'nutzt nur die Details, die du unten eingibst. Keine gespeicherten Unternehmensdaten werden verwendet.',
    'studio.brandName': 'Markenname',
    'studio.productName': 'Produktname',
    'studio.productDescription': 'Produktbeschreibung',
    'studio.productDescriptionPlaceholder': 'Beschreibe, was das Produkt leistet, wem es hilft und wofur es gedacht ist.',
    'studio.brandMemory': 'Markengedachtnis (optional)',
    'studio.brandColors': 'Markenfarben',
    'studio.brandFont': 'Markenschrift',
    'studio.knowledgeBase': 'Wissensbasis',
    'studio.targetAudience': 'Zielgruppe',
    'studio.targetAudiencePlaceholder': 'Wer benotigt oder kauft dieses Produkt am ehesten?',
    'studio.keyBenefits': 'Kernnutzen',
    'studio.keyBenefitsPlaceholder': 'Liste die wichtigsten Kundenvorteile durch Kommas getrennt auf.',
    'studio.specialOffer': 'Angebot',
    'studio.specialOfferPlaceholder': 'Optional — z. B. Einfuhrungsrabatt, Testphase oder zeitlich begrenztes Paket',
    'studio.reference': 'Wettbewerber / Referenz',
    'studio.referencePlaceholder': 'Optional — z. B. visuelle Stimmung, Marktvorbild oder Kampagnenstil',
    'studio.visualStyle': 'Gewunschter visueller Stil',
    'studio.visualInstructions': 'Zusätzliche visuelle Anweisungen',
    'studio.visualInstructionsPlaceholder': 'Beschreibe erforderliche Motive, Umgebung, Komposition, Materialien, Licht oder Produktverhalten.',
    'studio.negativeInstructions': 'Zu vermeidende Inhalte (Negativanweisungen)',
    'studio.negativeInstructionsPlaceholder': 'z. B. keine Personen, keine Smartphones, keine roten Hintergründe, keine Außenaufnahme',
    'studio.negativeInstructionsHelp': 'Diese Ausschlüsse werden dem finalen Bild-Prompt und dem Negativ-Prompt des Videomodells hinzugefügt.',
    'studio.goal': 'Ziel',
    'studio.tone': 'Ton',
    'studio.strategy': 'Strategie',
    'studio.platform': 'Plattform',
    'studio.language': 'Sprache',
    'studio.renderStyle': 'Render-Stil',
    'studio.artDirection': 'Art Direction',
    'studio.imageCreativeOptions': 'Bild-Creative-Optionen',
    'studio.videoDuration': 'Videodauer',
    'studio.videoSize': 'Video-Ausgabegröße',
    'studio.back': 'Zuruck',
    'studio.continue': 'Weiter',
    'studio.experiment': 'A/B-Experiment',
    'studio.running': 'Lauft...',
    'studio.generate': 'Kampagne generieren',
    'studio.generating': 'Generiert...',
  },
};

export function loadSettings(scope = 'visitor') {
  if (!isConsentGranted('functional')) return DEFAULT_SETTINGS;
  try {
    const key = scope === 'visitor' ? SETTINGS_STORAGE_KEY : `${SETTINGS_STORAGE_KEY}:${scope}`;
    const stored = JSON.parse(localStorage.getItem(key) || '{}');
    return { ...DEFAULT_SETTINGS, ...stored };
  } catch (e) {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(settings, scope = 'visitor') {
  if (!isConsentGranted('functional')) return;
  const key = scope === 'visitor' ? SETTINGS_STORAGE_KEY : `${SETTINGS_STORAGE_KEY}:${scope}`;
  localStorage.setItem(key, JSON.stringify(settings));
}

export function translate(language, key) {
  return TRANSLATIONS[language]?.[key] || TRANSLATIONS.en[key] || key;
}

export const SettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  updateSetting: () => {},
  resetSettings: () => {},
  t: (key) => translate(DEFAULT_SETTINGS.language, key),
});

export const useSettings = () => useContext(SettingsContext);
